import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Monitor,
  Disc,
  MessageSquare,
  PhoneOff,
} from 'lucide-react';

interface MediaControlsProps {
  cameraOn: boolean;
  microphoneOn: boolean;
  screenShareOn: boolean;
  isRecording?: boolean;
  onCameraToggle: (on: boolean) => void;
  onMicrophoneToggle: (on: boolean) => void;
  onScreenShareToggle: (on: boolean) => void;
  onRecordingToggle?: () => void;
  onLeaveOrEnd?: () => void;
  onOpenChat?: () => void;
  unreadChatCount?: number;
  isDoctor?: boolean;
  isDailyRoom?: boolean;
  disabled?: boolean;
}

export function MediaControls({
  cameraOn,
  microphoneOn,
  screenShareOn,
  isRecording = false,
  onCameraToggle,
  onMicrophoneToggle,
  onScreenShareToggle,
  onRecordingToggle,
  onLeaveOrEnd,
  onOpenChat,
  unreadChatCount = 0,
  isDoctor = false,
  isDailyRoom = false,
  disabled,
}: MediaControlsProps) {
  const btnBase =
    'flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full transition-all duration-200 cursor-pointer shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed shrink-0';
  const btnActive = 'bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700';
  const btnMuted = 'bg-rose-600/90 hover:bg-rose-700 text-white border border-rose-500 shadow-rose-900/30';
  const btnRecording = 'bg-red-600 text-white hover:bg-red-700 animate-pulse border border-red-500';

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3 px-3 py-2 bg-neutral-900/90 backdrop-blur-md border border-neutral-800 rounded-full shadow-2xl max-w-[95vw]">
      {/* Botões nativos de Câmera e Microfone quando NÃO for Daily.co */}
      {!isDailyRoom && (
        <>
          <button
            type="button"
            onClick={() => onCameraToggle(!cameraOn)}
            disabled={disabled}
            className={`${btnBase} ${cameraOn ? btnActive : btnMuted}`}
            title={cameraOn ? 'Desligar câmera' : 'Ligar câmera'}
            aria-label={cameraOn ? 'Desligar câmera' : 'Ligar câmera'}
          >
            {cameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>

          <button
            type="button"
            onClick={() => onMicrophoneToggle(!microphoneOn)}
            disabled={disabled}
            className={`${btnBase} ${microphoneOn ? btnActive : btnMuted}`}
            title={microphoneOn ? 'Mutar microfone' : 'Ativar microfone'}
            aria-label={microphoneOn ? 'Mutar microfone' : 'Ativar microfone'}
          >
            {microphoneOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </button>

          <button
            type="button"
            onClick={() => onScreenShareToggle(!screenShareOn)}
            disabled={disabled}
            className={`${btnBase} ${screenShareOn ? 'bg-blue-600 text-white' : btnActive} hidden sm:flex`}
            title={screenShareOn ? 'Parar compartilhamento' : 'Compartilhar tela'}
            aria-label={screenShareOn ? 'Parar compartilhamento' : 'Compartilhar tela'}
          >
            <Monitor className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Botão de Chat / Prontuário */}
      {onOpenChat && (
        <button
          type="button"
          onClick={onOpenChat}
          className={`${btnBase} ${btnActive} relative flex items-center gap-1.5 px-3 sm:px-4 w-auto rounded-full`}
          title="Abrir Chat & Prontuário"
          aria-label="Abrir Chat & Prontuário"
        >
          <MessageSquare className="w-5 h-5 text-blue-400 shrink-0" />
          <span className="text-xs font-semibold hidden sm:inline">
            {isDoctor ? 'Prontuário & Chat' : 'Chat & Exames'}
          </span>
          {unreadChatCount > 0 && (
            <span className="w-4 h-4 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce">
              {unreadChatCount}
            </span>
          )}
        </button>
      )}

      {/* Gravação (se habilitada para o médico) */}
      {onRecordingToggle && (
        <button
          type="button"
          onClick={onRecordingToggle}
          disabled={disabled}
          className={`${btnBase} ${isRecording ? btnRecording : btnActive} hidden sm:flex`}
          title={isRecording ? 'Parar gravação' : 'Gravar consulta'}
          aria-label={isRecording ? 'Parar gravação' : 'Gravar consulta'}
        >
          <Disc className={`w-5 h-5 ${isRecording ? 'text-white' : 'text-rose-400'}`} />
        </button>
      )}

      {/* Sair ou Encerrar Consulta (Botão vermelho de destaque direto na barra!) */}
      {onLeaveOrEnd && (
        <button
          type="button"
          onClick={onLeaveOrEnd}
          className={`${btnBase} bg-red-600 hover:bg-red-700 text-white border border-red-500 hover:scale-105 active:scale-95`}
          title={isDoctor ? 'Encerrar Consulta' : 'Sair da Consulta'}
          aria-label={isDoctor ? 'Encerrar Consulta' : 'Sair da Consulta'}
        >
          <PhoneOff className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}